import { useEffect, useRef, useState } from "react";
import { TextField } from "../../common/inputFiled";

const sanitizePollText = (value) =>
  String(value || "").replace(/[^A-Za-z0-9\s]/g, "");

// Duration Selection Modal
function DurationModal({ open, onClose, onSelectDuration }) {
  const modalRef = useRef();
  
  useEffect(() => {
    function handleOutside(e) {
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    }
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, onClose]);

  if (!open) return null;

  const durations = ["1 hour", "24 hours", "3 days", "7 days"];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/20">
      <div
        ref={modalRef}
        className="bg-white rounded-3xl p-6 w-80 shadow-xl flex flex-col gap-4"
      >
        <h2 className="text-lg font-semibold text-center">Poll Duration</h2>
        <p className="text-sm text-gray-600 text-center">How long should this poll be active?</p>
        
        <div className="space-y-2">
          {durations.map((duration) => (
            <button
              key={duration}
              className="w-full text-left px-4 py-3 text-sm border border-gray-200 rounded-xl hover:bg-purple-50 hover:border-purple-200"
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up
                onSelectDuration(duration);
              }}
            >
              {duration}
            </button>
          ))}
        </div>
        
        <button
          className="mt-4 text-gray-500 text-sm self-center"
          onClick={(e) => {
            e.stopPropagation(); // Prevent event from bubbling up
            onClose();
          }}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Main Poll Modal
export default function PollModal({ open, onClose, onSubmit }) {
  const modalRef = useRef();
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState([""]);
  const [duration, setDuration] = useState("24 hours");
  const [showDurationModal, setShowDurationModal] = useState(false);
  const [optionError, setOptionError] = useState("");
  const [allowMultiple, setAllowMultiple] = useState(false);
  
  // Close when clicking outside
  useEffect(() => {
    function handleOutside(e) {
      // If duration modal is open, don't close the main poll modal
      if (showDurationModal) return;
      if (modalRef.current && !modalRef.current.contains(e.target)) onClose();
    }
    if (open) document.addEventListener("mousedown", handleOutside);
    return () => document.removeEventListener("mousedown", handleOutside);
  }, [open, onClose, showDurationModal]);

  if (!open) return null;

  const addOption = () => {
    const last = options[options.length - 1] ?? "";
    if (last.trim() === "") {
      setOptionError("Please fill the previous option before adding a new one.");
      return;
    }
    setOptionError("");
    setOptions([...options, ""]);
  };
  const removeOption = (idx) =>
    setOptions(options.filter((_, i) => i !== idx));
  const updateOption = (idx, value) => {
    const sanitized = sanitizePollText(value);
    setOptions(options.map((o, i) => (i === idx ? sanitized : o)));
    // Clear guidance when user starts typing
    if (sanitized.trim() !== "") setOptionError("");
  };

  const lastOptionBlank = !options.length || options[options.length - 1].trim() === "";
  const isSubmitDisabled = !question.trim() || lastOptionBlank || options.filter(o => o.trim()).length < 2;

  const handleSubmit = () => {
    if (isSubmitDisabled) return;
    
    // Convert duration to milliseconds
    const durationMs = getDurationInMs(duration);
    const endTime = new Date(Date.now() + durationMs).toISOString();
    
    onSubmit({ 
      question: sanitizePollText(question).trim(), 
      options: options.map((o) => sanitizePollText(o).trim()).filter(Boolean), 
      timer: duration,
      allowMultiple: allowMultiple ? 1 : 0,
      startTime: new Date().toISOString(),
      endTime: endTime,
      durationMs: durationMs
    });
    setQuestion("");
    setOptions([""]);
    setDuration("24 hours");
    setAllowMultiple(false);
    setShowDurationModal(false);
    setOptionError("");
    onClose();
  };

  const getDurationInMs = (durationStr) => {
    const durations = {
      "1 hour": 1 * 60 * 60 * 1000,
      "24 hours": 24 * 60 * 60 * 1000,
      "3 days": 3 * 24 * 60 * 60 * 1000,
      "7 days": 7 * 24 * 60 * 60 * 1000
    };
    return durations[durationStr] || 24 * 60 * 60 * 1000;
  };

  const handleSelectDuration = (selectedDuration) => {
    setDuration(selectedDuration);
    setShowDurationModal(false);
  };

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/20">
        <div
          ref={modalRef}
          className="bg-white rounded-3xl p-6 w-96 shadow-xl flex flex-col gap-4"
        >
          <div className="flex justify-between items-center">
            <button 
              className="flex flex-col items-center text-[10px] text-gray-600 "
              onClick={(e) => {
                e.stopPropagation(); // Prevent event from bubbling up
                setShowDurationModal(true);
              }}
            >
              <div className="bg-[#5D5FEF] p-2 rounded-xl">
              <img src="/groupscreen/timer.svg" alt="" className="w-5 h-5" />
              </div>
              {duration}
            </button>
            
            <button
              className={`bg-[#5D5FEF] p-2 mb-2 text-white w-9 h-9 flex items-center justify-center rounded-xl ${isSubmitDisabled ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={isSubmitDisabled}
              onClick={handleSubmit}
            >
              ✓
            </button>
          </div>

          <h2 className="text-lg font-semibold text-center">Create Poll</h2>

          {/* <label className="text-sm font-medium">Question</label> */}
          {/* <input
            className="border rounded-xl p-2 focus:outline-none focus:ring-2 focus:ring-purple-400"
            placeholder="Ask Question"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
          /> */}
           <TextField
                      id="question"
                      type="Question"
                      name="Question"
                      label="Question"
                      placeholder="Ask Question"
                      value={question}
                      onChange={(e) => setQuestion(sanitizePollText(e.target.value))}
                    />

          <label className="text-sm font-medium">Options</label>
          {options.map((opt, idx) => (
            <div key={idx} className="flex items-center gap-2 mb-1">
              <input
                className="border flex-1 rounded-xl p-2 focus:outline-none focus:ring-purple-400 border-[#E6E2FF] focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10"
                placeholder="Add"
                value={opt}
                onChange={(e) => updateOption(idx, e.target.value)}
              />
              {options.length > 1 && (
                <button
                  onClick={() => removeOption(idx)}
                  className="text-gray-500 hover:text-red-500"
                >
                  ✕
                </button>
              )}
            </div>
          ))}
          <button
            onClick={addOption}
            className="text-purple-600 text-sm self-start mt-1 disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={options[options.length - 1].trim() === ""}
          >
            + Add option
          </button>
          {optionError && (
            <p className="text-red-500 text-xs mt-1">{optionError}</p>
          )}

          {/* Multiple Selection Toggle */}
          <div className="flex items-center gap-3 mt-4 pt-4 border-t border-gray-200">
            <div className="flex-1">
              <label className="text-sm font-medium text-gray-900">Allow Multiple Selections</label>
              <p className="text-xs text-gray-500">Users can select more than one option</p>
            </div>
            <button
              onClick={() => setAllowMultiple(!allowMultiple)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                allowMultiple ? "bg-purple-600" : "bg-gray-300"
              }`}
            >
              <span
                className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${
                  allowMultiple ? "translate-x-6" : "translate-x-1"
                }`}
              />
            </button>
          </div>
        </div>
      </div>
      <DurationModal
        open={showDurationModal}
        onClose={() => setShowDurationModal(false)}
        onSelectDuration={handleSelectDuration}
      />
    </>
  );
}